import * as store from '@/store';
import Icon, {IconSize} from '@/components/Icon';
import {ArrowRightSvg, DialogCloseSvg} from '@/icons';
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from '@/interface';
import TemplateCard from './TemplateCard';
import classNames from 'classnames';
import fold from '@/assets/img/fold.gif';
import unfold from '@/assets/img/unfold.gif';
import moreTemplate from '@/assets/img/moreTemplate.png';
import TemplateModal from './TemplateCenter';
import {closeTemplateRecommendBuried, usedTemplateBuried} from '@/buried/common';
import {getMorphoService, serviceNames} from '@/rte/editor';
import styles from './index.module.scss';
import {ITpls} from './useTemplateList';

interface ITemplate {
    className?: string;
    onClick: (template: Template, onClose: () => void) => void;
    docContentWidth?: {
        width: number;
        left: number;
    };
    isAllowShowTemplate: boolean;
    showDefaultTemplate: boolean;
    setShowTemplate: (val: boolean) => void;
    setIsAllowShowTemplate: (val: boolean) => void;
    curTpls: ITpls;
    isCardView?: boolean;

}

const EDITOR_PADDING = 100;

function TemplateRecommend({
    className,
    onClick,
    docContentWidth,
    isAllowShowTemplate,
    curTpls,
    showDefaultTemplate,
    setShowTemplate,
    setIsAllowShowTemplate,
    isCardView,
}: ITemplate) {
    const [templateCardNumber, setTemplateCardNumber] = useState(5);
    const [visible, setVisible] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);
    const templateModalRef = useRef<any>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const moreRef = useRef<HTMLDivElement>(null);
    const {userTpls = [], sysTpls = []} = curTpls || {};


    const recommendTemplates = useMemo(() => ([
        ...userTpls.slice(0, 1),
        ...sysTpls.slice(0, userTpls.length >= 1 ? 4 : (4 + (1 - userTpls.length))),
    ]), [userTpls, sysTpls]);

    useEffect(() => {
        if (!docContentWidth) {
            return;
        }
        const {width} = docContentWidth;
        const MAX_SPACE = 20;
        const MIN_SPACE = 10;
        const cardWidth = 150;
        const gapWidth = width - cardWidth - EDITOR_PADDING;
        const cardMinNum = Math.floor(gapWidth / (cardWidth + MAX_SPACE));
        const templateCardNumber = cardMinNum >= 0 ? cardMinNum : 0;
        const cardMaxNum = Math.floor(gapWidth / (cardWidth + MIN_SPACE));
        const templateCardMaxNumber = cardMaxNum >= 0 ? cardMaxNum : 0;

        setTemplateCardNumber(templateCardMaxNumber > templateCardNumber ? templateCardMaxNumber : templateCardNumber);
    }, [docContentWidth]);


    function handleHide() {
        setIsAllowShowTemplate(false);
        closeTemplateRecommendBuried();
    }

    function handleMouseEnter() {
        imgRef.current!.src = unfold + `?${Date.now()}`;
    }

    function handleMouseLeave() {
        imgRef.current!.src = fold + `?${Date.now()}`;
    }

    function handleOpenModal() {
        setVisible(true);

    }

    function handleCloseModal() {
        setVisible(false);
    }

    const handleOpenPreviewModal = useCallback((tempalteItem: Template) => {
        if (!templateModalRef.current) {
            return;
        }
        handleOpenModal();
        templateModalRef.current.handleGoPreview(tempalteItem);
    }, []);

    const handleUseTemplate = useCallback((tempalteItem: Template) => {
        return (e: any) => {
            // 添加nodes参数，用于埋点
            const editor = getMorphoService(serviceNames.IEditorStateService).editor;
            const nodes = editor.commonUtils.calcNodeWithTypes(editor, {children: tempalteItem.content!.children}, {
                distinguishBlockLayout: true,
                serializeToString: true,
            });
            onClick && onClick(tempalteItem, handleCloseModal);
            usedTemplateBuried(tempalteItem.id, tempalteItem.name as string, nodes);
            e.stopPropagation();
        };
    }, [onClick]);

    const handleVisible = useCallback((visible: boolean) => {
        setVisible(visible);
    }, []);

    useEffect(() => {
        if (isAllowShowTemplate) {
            return;
        }
        function handleClose() {
            setShowTemplate(false);
        }

        const cardDom = cardRef.current;
        const moreDom = moreRef.current;
        if (cardDom) {
            cardDom.addEventListener('animationend', handleClose);
        } else {
            moreDom && moreDom?.addEventListener('animationend', handleClose);
        }
        return () => {
            if (cardDom) {
                cardDom.removeEventListener('animationend', handleClose);
            } else {
                moreDom && moreDom?.removeEventListener('animationend', handleClose);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAllowShowTemplate]);

    return <>
        <div
            className={classNames(
                styles.wrap,
                !showDefaultTemplate && 'fadeOut',
                className,
                // pm要求卡片视图下，当编辑区域小于指定宽度时隐藏推荐模板卡片
                (docContentWidth!.width <= 240 && isCardView) && styles.hideWrap
            )}
            style={{maxWidth: docContentWidth!.width - EDITOR_PADDING}}
        >
            <div className={styles.header}>
                <Icon
                    className={classNames(styles.close, isAllowShowTemplate ? 'fadeIn' : 'fadeOut')}
                    svg={DialogCloseSvg}
                    size={IconSize.XXL}
                    onClick={handleHide}
                    noSpace
                />
            </div>
            <div className={styles.content}>
                {
                    recommendTemplates.slice(0, templateCardNumber).map((tepItem, index) => {

                        const delayStyle = {animationDelay: `${(templateCardNumber - index) * 66}ms`};
                        return <div
                            className={classNames(
                                styles.cardItem,
                                isAllowShowTemplate ? styles.cardFadeIn : styles.cardFadeOut
                            )}
                            ref={index === 0 ? cardRef : null}
                            style={isAllowShowTemplate ? {} : delayStyle}
                            key={tepItem.id}
                        >
                            <TemplateCard
                                className={classNames(
                                    styles.card
                                )}
                                data={tepItem}
                                openPreview={handleOpenPreviewModal}
                                onUseTemplate={handleUseTemplate}
                                onClickCard={handleUseTemplate}
                            />
                            <i className={styles.space} />
                        </div>;
                    })
                }
                <div
                    className={classNames(
                        styles.more,
                        styles.card,
                        isAllowShowTemplate ? styles.cardFadeIn : styles.cardFadeOut
                    )}
                    // key是为了在切换视图时更多模版再次出现动画
                    key={recommendTemplates.length ? JSON.stringify(recommendTemplates[0].rteId) : null}
                    ref={moreRef}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={handleOpenModal}
                >
                    <div className={styles.moreCard}>
                        <img ref={imgRef} src={moreTemplate} />
                        <div className={styles.moreText}>
                            更多模版
                            <Icon svg={ArrowRightSvg} size={IconSize.XS} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <TemplateModal
            ref={templateModalRef}
            sysTpls={sysTpls}
            userTpls={userTpls}
            visible={visible}
            onVisibleChange={handleVisible}
            onUseTemplate={handleUseTemplate}
        />
    </>;
}

export default store.connect([
    store.KEYS.DOC_CONTENT_WIDTH,
    store.KEYS.IS_CARD_VIEW,
])(TemplateRecommend);
